import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';
import { TrendingUp, Music, Search, X } from 'lucide-react';

const COLORS = ['#E94560', '#0F3460', '#16C79A', '#F5A623', '#7B61FF', '#FF6B6B', '#4ECDC4', '#45B7D1'];

export default function StreamingPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [allPlatforms, setAllPlatforms] = useState([]);
  const [allTracks, setAllTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterTrack, setFilterTrack] = useState('');
  const [filterTrackLabel, setFilterTrackLabel] = useState('');
  const [filterPlatform, setFilterPlatform] = useState('');
  // Search
  const [searchText, setSearchText] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchRef = useRef(null);
  const searchTimeout = useRef(null);

  // Initial load - get all data unfiltered
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data: d } = await api.get('/streaming/stats');
        setData(d);
        setAllPlatforms(d.platforms || []);
        setAllTracks(d.top_tracks || []);
      } catch (e) { toast.error('Errore'); }
      finally { setLoading(false); }
    })();
  }, []);

  // Filtered load
  useEffect(() => {
    if (!filterTrack && !filterPlatform) return;
    (async () => {
      try {
        let url = '/streaming/stats?';
        if (filterPlatform) url += `platform=${encodeURIComponent(filterPlatform)}&`;
        if (filterTrack) url += `isrc=${encodeURIComponent(filterTrack)}&`;
        const { data: d } = await api.get(url);
        setData(d);
      } catch (e) {}
    })();
  }, [filterTrack, filterPlatform]);

  // Search autocomplete
  useEffect(() => {
    if (searchText.length < 2) { setSuggestions([]); return; }
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(async () => {
      try {
        const { data: d } = await api.get(`/streaming/stats?search=${encodeURIComponent(searchText)}`);
        setSuggestions(d.suggestions || []);
        setShowSuggestions(true);
      } catch (e) {}
    }, 300);
  }, [searchText]);

  // Click outside closes suggestions
  useEffect(() => {
    const h = (e) => { if (searchRef.current && !searchRef.current.contains(e.target)) setShowSuggestions(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const selectTrack = (t) => {
    setFilterTrack(t.isrc);
    setFilterTrackLabel(`${t.artist_name ? t.artist_name + ' - ' : ''}${t.title}`);
    setSearchText('');
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const clearFilters = () => {
    setFilterTrack('');
    setFilterTrackLabel('');
    setFilterPlatform('');
    setSearchText('');
    // Reload all
    (async () => {
      try {
        const { data: d } = await api.get('/streaming/stats');
        setData(d);
      } catch (e) {}
    })();
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-pn-red" /></div>;

  const totalStreams = data?.daily?.reduce((s, d) => s + (parseInt(d.streams) || 0), 0) || 0;
  const totalRevenue = data?.daily?.reduce((s, d) => s + (parseFloat(d.revenue) || 0), 0) || 0;

  return (
    <div>
      <h1 className="text-2xl font-bold text-pn-dark mb-2">Storico Ascolti</h1>
      <p className="text-xs text-gray-400 bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2 mb-4">I dati provengono dai rendiconti. Le piattaforme di streaming hanno un ritardo medio di circa 3 mesi nel fornire i report di vendita.</p>

      {/* Search + Filters */}
      <div className="flex gap-3 mb-4 flex-wrap items-center">
        {/* Search bar with autocomplete */}
        <div ref={searchRef} className="relative flex-1 min-w-[250px]">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              className="input-field w-full pl-9 pr-8 text-sm"
              placeholder="Cerca brano o artista..."
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
            />
            {searchText && <button onClick={() => { setSearchText(''); setSuggestions([]); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"><X size={14} /></button>}
          </div>
          {showSuggestions && suggestions.length > 0 && (
            <div className="absolute top-full mt-1 w-full bg-white rounded-xl shadow-xl border z-50 max-h-60 overflow-y-auto">
              {suggestions.map((s, i) => (
                <div key={i} onClick={() => selectTrack(s)} className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b last:border-0">
                  <p className="text-sm font-medium">{s.title || s.isrc}</p>
                  {s.artist_name && <p className="text-xs text-gray-400">{s.artist_name}</p>}
                </div>
              ))}
            </div>
          )}
        </div>

        {allTracks.length > 0 && (
          <select className="input-field w-64 text-sm" value={filterTrack} onChange={e => { const t = allTracks.find(x => x.isrc === e.target.value); setFilterTrack(e.target.value); setFilterTrackLabel(t ? `${t.artist_name ? t.artist_name + ' - ' : ''}${t.title}` : ''); }}>
            <option value="">Tutti i brani</option>
            {allTracks.map((t, i) => <option key={i} value={t.isrc}>{t.artist_name ? `${t.artist_name} - ` : ''}{t.title}</option>)}
          </select>
        )}

        {allPlatforms.length > 0 && (
          <select className="input-field w-48 text-sm" value={filterPlatform} onChange={e => setFilterPlatform(e.target.value)}>
            <option value="">Tutte le piattaforme</option>
            {allPlatforms.map((p, i) => <option key={i} value={p.platform}>{p.platform}</option>)}
          </select>
        )}

        {(filterTrack || filterPlatform) && (
          <button onClick={clearFilters} className="text-xs text-pn-red hover:underline">Reset filtri</button>
        )}
      </div>

      {/* Active filter badge */}
      {filterTrackLabel && (
        <div className="flex items-center gap-2 mb-4">
          <span className="bg-pn-red/10 text-pn-red text-xs px-3 py-1.5 rounded-full font-medium flex items-center gap-1">
            <Music size={12} /> {filterTrackLabel}
            <button onClick={() => { setFilterTrack(''); setFilterTrackLabel(''); clearFilters(); }} className="ml-1 hover:text-red-800"><X size={12} /></button>
          </span>
        </div>
      )}

      {/* Stats cards */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="card p-4"><div className="flex items-center gap-3"><TrendingUp size={20} className="text-pn-red" /><div><p className="text-sm text-gray-500">Totale Stream</p><p className="text-2xl font-bold">{totalStreams.toLocaleString()}</p></div></div></div>
        <div className="card p-4"><div className="flex items-center gap-3"><Music size={20} className="text-green-500" /><div><p className="text-sm text-gray-500">Guadagno Totale</p><p className="text-2xl font-bold text-green-600">€{totalRevenue.toFixed(2)}</p></div></div></div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card p-6">
          <h2 className="font-bold mb-4">Andamento</h2>
          {data?.daily?.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data.daily}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="stat_date" tick={{ fontSize: 10 }} tickFormatter={d => { const parts = d.split('-'); return `${parts[1]}/${parts[0].slice(2)}`; }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip labelFormatter={d => { const parts = d.split('-'); const months = ['Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic']; return `${months[parseInt(parts[1])-1]} ${parts[0]}`; }} />
                <Bar dataKey="streams" fill="#E94560" radius={[4, 4, 0, 0]} name="Stream" />
              </BarChart>
            </ResponsiveContainer>
          ) : <div className="h-48 flex items-center justify-center text-gray-400">Nessun dato</div>}
        </div>
        <div className="card p-6">
          <h2 className="font-bold mb-4">Per Piattaforma</h2>
          {data?.platforms?.length > 0 ? (<>
            <ResponsiveContainer width="100%" height={180}>
              <PieChart><Pie data={data.platforms.map(p => ({...p, streams: parseInt(p.streams) || 0}))} dataKey="streams" nameKey="platform" cx="50%" cy="50%" outerRadius={70}>{data.platforms.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip /></PieChart>
            </ResponsiveContainer>
            <div className="space-y-2 mt-2">{data.platforms.map((p, i) => (
              <div key={i} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 rounded px-1 py-0.5" onClick={() => setFilterPlatform(p.platform)}>
                <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
                <span className="flex-1 truncate">{p.platform}</span>
                <span className="font-medium">{parseInt(p.streams).toLocaleString()}</span>
              </div>
            ))}</div>
          </>) : <div className="h-48 flex items-center justify-center text-gray-400">Nessun dato piattaforme</div>}
        </div>
      </div>

      {/* Top Tracks */}
      {data?.top_tracks?.length > 0 && (
        <div className="card p-6 mt-6">
          <h2 className="font-bold mb-4">Top Brani</h2>
          <div className="space-y-2">{data.top_tracks.map((t, i) => (
            <div key={i} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded-lg cursor-pointer" onClick={() => selectTrack(t)}>
              <span className="w-6 text-gray-400 font-bold text-sm">{i + 1}</span>
              <Music size={16} className="text-pn-red" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{t.title || t.isrc}</p>
                {t.artist_name && <p className="text-xs text-gray-400">{t.artist_name}</p>}
              </div>
              <span className="text-sm text-gray-500">{parseInt(t.total_streams).toLocaleString()}</span>
              <span className="text-sm font-medium text-green-600">€{parseFloat(t.total_revenue || 0).toFixed(2)}</span>
            </div>
          ))}</div>
        </div>
      )}
    </div>
  );
}
